import { useState, useRef, useCallback } from 'react'
import { FaHeart, FaRegHeart, FaThumbsUp, FaStar } from 'react-icons/fa'
import { FaHeartCirclePlus } from 'react-icons/fa6'
import profiles from '../data/profiles.json'
import { mainImages, profileImages } from '../assets/images'

const mainBg = mainImages.bg
const headerText = mainImages.text

// Helper to get the imported image for a profile
const getProfileImage = (imagePath: string): string => {
  return profileImages[imagePath] || imagePath
}

interface Profile {
  id: number
  name: string
  age: number
  distance: number
  image: string
  tags: string[]
  online: boolean
}

export default function MainView() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [noTransition, setNoTransition] = useState(false)
  const [tagsVisible, setTagsVisible] = useState(true)
  const [isButtonSwipe, setIsButtonSwipe] = useState(false)

  const dragStart = useRef({ x: 0, y: 0 })
  const cardRef = useRef<HTMLDivElement>(null)

  // Cycle through profiles
  const currentProfile = profiles[currentIndex % profiles.length] as Profile
  const nextProfile = profiles[(currentIndex + 1) % profiles.length] as Profile

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    if (isExiting) return
    setIsDragging(true)
    dragStart.current = { x: clientX, y: clientY }
  }, [isExiting])

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging || isExiting) return
    const deltaX = clientX - dragStart.current.x
    const deltaY = clientY - dragStart.current.y
    setDragOffset({ x: deltaX, y: deltaY * 0.3 }) // Reduce vertical movement
  }, [isDragging, isExiting])

  const handleDragEnd = useCallback(() => {
    if (!isDragging || isExiting) return
    setIsDragging(false)

    const threshold = 100 // Minimum swipe distance to trigger action

    if (dragOffset.x > threshold) {
      // Swipe right - Like
      triggerSwipe('right')
    } else if (dragOffset.x < -threshold) {
      // Swipe left - Pass
      triggerSwipe('left')
    } else {
      // Reset position
      setDragOffset({ x: 0, y: 0 })
    }
  }, [isDragging, isExiting, dragOffset.x])

  const triggerSwipe = useCallback((direction: 'left' | 'right') => {
    setIsExiting(true)
    setIsDragging(false)
    setTagsVisible(false) // Fade out tags

    // Animate current card off screen
    const targetX = direction === 'right' ? window.innerWidth + 200 : -window.innerWidth - 200
    setDragOffset({ x: targetX, y: dragOffset.y })

    // After animation completes, switch to next profile
    setTimeout(() => {
      // Disable transition, reset position, change index, then re-enable
      setNoTransition(true)
      setDragOffset({ x: 0, y: 0 })
      setCurrentIndex(prev => prev + 1)
      setIsExiting(false)
      // Re-enable transition after a frame
      requestAnimationFrame(() => {
        setNoTransition(false)
      })
      // Fade in tags after new card is in place
      setTimeout(() => {
        setTagsVisible(true)
      }, 50)
    }, 400)
  }, [dragOffset.y])

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientX, e.touches[0].clientY)
  }

  const handleTouchEnd = () => {
    handleDragEnd()
  }

  // Mouse event handlers (for desktop testing)
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientX, e.clientY)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    handleDragMove(e.clientX, e.clientY)
  }

  const handleMouseUp = () => {
    handleDragEnd()
  }

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd()
    }
  }

  // Button handlers - slower animation for buttons
  const handlePass = () => {
    if (!isExiting) {
      triggerButtonSwipe('left')
    }
  }

  const handleLike = () => {
    if (!isExiting) {
      triggerButtonSwipe('right')
    }
  }

  const handleSuperLike = () => {
    if (!isExiting) {
      triggerButtonSwipe('right')
    }
  }

  const triggerButtonSwipe = useCallback((direction: 'left' | 'right') => {
    setIsExiting(true)
    setTagsVisible(false) // Fade out tags
    setIsButtonSwipe(true) // Mark as button-triggered for slower animation

    // Animate current card off screen (slower for button presses)
    const targetX = direction === 'right' ? window.innerWidth + 200 : -window.innerWidth - 200
    setDragOffset({ x: targetX, y: 0 })

    // After animation completes, switch to next profile (longer timeout for slower animation)
    setTimeout(() => {
      // Disable transition, reset position, change index, then re-enable
      setNoTransition(true)
      setDragOffset({ x: 0, y: 0 })
      setCurrentIndex(prev => prev + 1)
      setIsExiting(false)
      setIsButtonSwipe(false)
      // Re-enable transition after a frame
      requestAnimationFrame(() => {
        setNoTransition(false)
      })
      // Fade in tags after new card is in place
      setTimeout(() => {
        setTagsVisible(true)
      }, 50)
    }, 700)
  }, [])

  // Calculate rotation based on drag
  const rotation = dragOffset.x * 0.05

  // Calculate next card scale based on swipe progress (0.95 -> 1.0)
  const swipeProgress = Math.min(Math.abs(dragOffset.x) / 150, 1)
  const nextCardScale = 0.95 + (0.05 * swipeProgress)
  const nextCardOpacity = 0.9 + (0.1 * swipeProgress)

  // Tags fade out as you swipe (1 -> 0)
  const tagsOpacity = tagsVisible ? (1 - swipeProgress) : 0

  // Show indicator overlays
  const showLikeIndicator = dragOffset.x > 50
  const showNopeIndicator = dragOffset.x < -50

  return (
    <div style={styles.container}>
      {/* Background */}
      <img src={mainBg} alt="" style={styles.bgImage} />

      {/* Content overlay */}
      <div style={styles.content}>
        {/* Row 1: Header text (10%) */}
        <div style={styles.row1}>
          <img src={headerText} alt="Flört" style={styles.headerTextImg} />
        </div>

        {/* Row 2: Buttons (5%) */}
        <div style={styles.row2}>
          <button style={styles.primaryButton}>
            <FaHeart style={styles.buttonIcon} />
            <span>Für dich</span>
          </button>
          <button style={styles.secondaryButton}>
            <FaHeartCirclePlus style={styles.secondaryButtonIcon} />
            <span>Double Date</span>
          </button>
        </div>

        {/* Row 3: Main image card (55%) */}
        <div style={styles.row3}>
          {/* Next card (underneath) - scales up as current card is swiped */}
          <div
            style={{
              ...styles.imageCard,
              ...styles.nextCard,
              transform: `scale(${nextCardScale})`,
              opacity: nextCardOpacity,
              transition: (isDragging || noTransition) ? 'none' : 'transform 0.3s ease-out, opacity 0.3s ease-out'
            }}
          >
            <img src={getProfileImage(nextProfile.image)} alt="" style={styles.cardImage} />
            {nextProfile.online && <div style={styles.onlinePill}>online</div>}
            <div style={styles.bottomLeftText}>{nextProfile.name}, {nextProfile.age} <span style={styles.pronouns}>er/ihm</span></div>
            <div style={styles.topRightText}>{nextProfile.distance} km entfernt</div>
          </div>

          {/* Current card (swipeable) - key forces remount on index change */}
          <div
            key={currentIndex}
            ref={cardRef}
            style={{
              ...styles.imageCard,
              ...styles.swipeableCard,
              transform: `translateX(${dragOffset.x}px) translateY(${dragOffset.y}px) rotate(${rotation}deg)`,
              transition: (isDragging || noTransition) ? 'none' : `transform ${isButtonSwipe ? '0.7s cubic-bezier(0.4, 0, 1, 1)' : '0.4s ease-out'}`,
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <img src={getProfileImage(currentProfile.image)} alt="" style={styles.cardImage} />

            {/* Like indicator */}
            {showLikeIndicator && (
              <div style={styles.likeIndicator}>LIKE</div>
            )}

            {/* Nope indicator */}
            {showNopeIndicator && (
              <div style={styles.nopeIndicator}>NOPE</div>
            )}

            {/* Online pill */}
            {currentProfile.online && <div style={styles.onlinePill}>online</div>}

            {/* Bottom left text */}
            <div style={styles.bottomLeftText}>{currentProfile.name}, {currentProfile.age} <span style={styles.pronouns}>er/ihm</span></div>

            {/* Bottom right text */}
            <div style={styles.topRightText}>{currentProfile.distance} km entfernt</div>
          </div>
        </div>

        {/* Row 4: Tags */}
        <div style={styles.row4}>
          <div style={{
            ...styles.tagsContainer,
            opacity: tagsOpacity,
            transition: (isDragging || isExiting) ? 'none' : 'opacity 0.15s ease-in-out'
          }}>
            {currentProfile.tags.map((tag, index) => (
              <span key={index} style={styles.tag}>{tag}</span>
            ))}
          </div>
        </div>

        {/* Row 5: Action buttons (20%) */}
        <div style={styles.row5}>
          <button
            style={{ ...styles.actionButton, backgroundColor: '#8B6878' }}
            onClick={handlePass}
          >
            <FaThumbsUp style={{ ...styles.actionIcon, transform: 'rotate(180deg)' }} />
          </button>
          <button
            style={{ ...styles.actionButton, backgroundColor: '#E8B878' }}
            onClick={handleSuperLike}
          >
            <FaStar style={styles.actionIcon} />
          </button>
          <button
            style={{ ...styles.actionButton, backgroundColor: '#D4467A' }}
            onClick={handleLike}
          >
            <FaRegHeart style={styles.actionIcon} />
          </button>
        </div>
      </div>
    </div>
  )
}

const PRIMARY_COLOR = '#EB5BAB'
const SECONDARY_BORDER_COLOR = '#B8458A' // 3 steps darker

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    backgroundColor: '#000'
  },
  bgImage: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    objectFit: 'cover',
    objectPosition: 'top center',
    zIndex: 0
  },
  content: {
    position: 'relative',
    width: '100%',
    height: '100%',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: 'calc(env(safe-area-inset-top) + 2px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom) + 10px)',
    paddingLeft: 'calc(env(safe-area-inset-left) + 15px)',
    paddingRight: 'calc(env(safe-area-inset-right) + 15px)'
  },
  // Row 1: 10%
  row1: {
    height: '10%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerTextImg: {
    height: '115%',
    width: 'auto',
    objectFit: 'contain'
  },
  // Row 2: 5%
  row2: {
    height: '5%',
    flexShrink: 0,
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  primaryButton: {
    flex: 1,
    height: '100%',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: PRIMARY_COLOR,
    border: `1.5px solid ${PRIMARY_COLOR}`,
    borderRadius: '15px',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
    fontWeight: 600,
    fontSize: '18px',
    cursor: 'pointer',
    padding: '0 15px'
  },
  buttonIcon: {
    fontSize: '18px',
    color: '#fff'
  },
  secondaryButton: {
    flex: 1,
    height: '100%',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: `1.5px solid ${SECONDARY_BORDER_COLOR}`,
    borderRadius: '15px',
    color: SECONDARY_BORDER_COLOR,
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
    fontWeight: 600,
    fontSize: '18px',
    cursor: 'pointer',
    padding: '0 15px'
  },
  secondaryButtonIcon: {
    fontSize: '18px',
    color: SECONDARY_BORDER_COLOR
  },
  // Row 3: 55%
  row3: {
    height: '55%',
    flexShrink: 0,
    paddingTop: '10px',
    paddingBottom: '10px',
    position: 'relative'
  },
  imageCard: {
    width: '100%',
    height: '100%',
    borderRadius: '25px',
    overflow: 'hidden',
    position: 'absolute',
    top: '10px',
    left: 0,
    userSelect: 'none'
  },
  nextCard: {
    zIndex: 1
  },
  swipeableCard: {
    zIndex: 2,
    touchAction: 'none'
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top left',
    pointerEvents: 'none'
  },
  likeIndicator: {
    position: 'absolute',
    top: '45%',
    left: '30px',
    padding: '8px 16px',
    border: '4px solid #00ff00',
    borderRadius: '10px',
    color: '#00ff00',
    fontSize: '32px',
    fontWeight: 800,
    transform: 'translateY(-50%) rotate(-20deg)',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
  },
  nopeIndicator: {
    position: 'absolute',
    top: '45%',
    right: '30px',
    padding: '8px 16px',
    border: '4px solid #ff0000',
    borderRadius: '10px',
    color: '#ff0000',
    fontSize: '32px',
    fontWeight: 800,
    transform: 'translateY(-50%) rotate(20deg)',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
  },
  onlinePill: {
    position: 'absolute',
    top: '15px',
    left: '15px',
    backgroundColor: '#1b8800',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
    fontWeight: 500,
    fontSize: '15px'
  },
  bottomLeftText: {
    position: 'absolute',
    bottom: '10px',
    left: '15px',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
    fontWeight: 700,
    fontSize: '31px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.8), 0 2px 4px rgba(0, 0, 0, 0.6), 0 4px 8px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 0, 0, 0.5)'
  },
  pronouns: {
    fontSize: '13px',
    fontWeight: 400,
    opacity: 0.8
  },
  topRightText: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
    fontWeight: 700,
    fontSize: '19px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.8), 0 2px 4px rgba(0, 0, 0, 0.6), 0 4px 8px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 0, 0, 0.5)'
  },
  // Row 4: flexible - grows to fill remaining space
  row4: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '8px',
    overflow: 'hidden'
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  tag: {
    backgroundColor: PRIMARY_COLOR,
    color: '#fff',
    padding: '6px 16px',
    borderRadius: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
    fontWeight: 500,
    fontSize: '17px'
  },
  // Row 5: 12%
  row5: {
    height: '12%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '25px'
  },
  actionButton: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0
  },
  actionIcon: {
    fontSize: '30px',
    color: '#fff',
    display: 'block',
    lineHeight: 1
  }
}
